// Billing Admin Utilities
// Manages billing admin role checks and company slot addon pricing

import type { User, SubscriptionPlan } from '../types';
import type { CompanySlotAddon } from '../types/billing-admin';

// ===== BILLING ADMIN ROLE CHECKS =====

/**
 * Check if a user is a Billing Admin
 * Supports both new roles array and legacy isBillingAdmin flag
 */
export function isBillingAdmin(user: User | null | undefined): boolean {
  if (!user) return false;
  
  // Check roles array first (new system)
  if (user?.roles && Array.isArray(user.roles)) {
    return user.roles.includes('billingAdmin') || user.roles.includes('billing_admin');
  }
  
  // Fallback to legacy single role field
  if (user?.role === 'billingAdmin' || user?.role === 'billing_admin') {
    return true;
  }
  
  // Fallback to legacy isBillingAdmin flag
  return user?.isBillingAdmin === true;
}

/**
 * Check if a user can manage billing (is Billing Admin)
 */
export function canManageBilling(user: User | null | undefined): boolean {
  return isBillingAdmin(user);
}

/**
 * Check if a user can see credit card info (Billing Admins only)
 */
export function canSeeCreditCardInfo(user: User | null | undefined): boolean {
  return isBillingAdmin(user);
}

/**
 * Check if a user can approve upgrade requests (Billing Admins only)
 */
export function canApproveUpgrades(user: User | null | undefined): boolean {
  return isBillingAdmin(user);
}

/**
 * Check if a user can cancel subscription (Billing Admins only)
 */
export function canCancelSubscription(user: User | null | undefined): boolean {
  return isBillingAdmin(user);
}

/**
 * Check if Billing Admin role can be reassigned for a given plan
 * Solo plan: Billing Admin cannot be reassigned (always the solo user)
 * Other plans: Billing Admin can be reassigned by current Billing Admin
 */
export function canReassignBillingAdmin(plan: SubscriptionPlan): boolean {
  return plan !== 'solo';
}

/**
 * Check if a user can reassign the Billing Admin role
 * Only the current Billing Admin can reassign (and only if not Solo plan)
 */
export function canUserReassignBillingAdmin(
  user: User | null | undefined,
  firmPlan: SubscriptionPlan
): boolean {
  if (!user) return false;
  return isBillingAdmin(user) && canReassignBillingAdmin(firmPlan);
}

/**
 * Check if a firm requires separate Billing Admin assignment during signup
 * Premium and Enterprise plans should prompt for separate Billing Admin
 */
export function shouldPromptForBillingAdmin(plan: SubscriptionPlan): boolean {
  return plan === 'premium' || plan === 'enterprise';
}

// ===== COMPANY SLOT ADDON PRICING =====

export interface CompanySlotAddonPack {
  planId: SubscriptionPlan;
  slotCount: number;
  pricePerMonth: number; // in cents
  percentOfBasePlan: number; // for display purposes
}

/**
 * Company slot addon pricing by plan
 * Based on pack-based model with decreasing percentage as tiers increase
 */
export const COMPANY_SLOT_ADDON_PRICING: CompanySlotAddonPack[] = [
  {
    planId: 'solo',
    slotCount: 5,
    pricePerMonth: 9900, // $99/mo
    percentOfBasePlan: 33,
  },
  {
    planId: 'starter',
    slotCount: 10,
    pricePerMonth: 14900, // $149/mo
    percentOfBasePlan: 30,
  },
  {
    planId: 'premium',
    slotCount: 25,
    pricePerMonth: 29900, // $299/mo
    percentOfBasePlan: 23,
  },
  {
    planId: 'enterprise',
    slotCount: 50,
    pricePerMonth: 79900, // $799/mo
    percentOfBasePlan: 16,
  },
];

/**
 * Get company slot addon pack for a plan
 */
export function getCompanySlotAddonPack(
  plan: SubscriptionPlan
): CompanySlotAddonPack | null {
  const pack = COMPANY_SLOT_ADDON_PRICING.find(p => p.planId === plan);
  return pack || null;
}

/**
 * Calculate total price for company slot addons
 */
export function calculateCompanySlotAddonsPrice(
  plan: SubscriptionPlan,
  addonPacks: number
): number {
  const pack = getCompanySlotAddonPack(plan);
  if (!pack) return 0;
  return pack.pricePerMonth * addonPacks;
}

/**
 * Format company slot addon price for display
 */
export function formatCompanySlotAddonPrice(plan: SubscriptionPlan): string {
  const pack = getCompanySlotAddonPack(plan);
  if (!pack) return '';
  
  const price = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  }).format(pack.pricePerMonth / 100);
  
  return `${price}/mo for +${pack.slotCount} companies`;
}

/**
 * Get total active company slots for a firm (base + addons)
 */
export function calculateTotalCompanySlots(
  basePlan: SubscriptionPlan,
  userCount: number,
  addons: CompanySlotAddon[],
  multiplier: number = 5
): number {
  // Base slots from plan
  const baseSlots = userCount * multiplier;
  
  // Additional slots from addons
  const addonSlots = addons.reduce((sum, addon) => sum + addon.slotCount, 0);
  
  return baseSlots + addonSlots;
}

/**
 * Check if firm is at or over capacity
 */
export function isAtCompanyCapacity(
  activeCompanyCount: number,
  totalSlots: number
): boolean {
  return activeCompanyCount >= totalSlots;
}

/**
 * Check if firm needs to upgrade or buy addons
 */
export function needsMoreCompanySlots(
  activeCompanyCount: number,
  totalSlots: number
): boolean {
  return activeCompanyCount >= totalSlots;
}

/**
 * Get recommended action when at capacity
 */
export function getCapacityRecommendation(
  currentPlan: SubscriptionPlan,
  userCount: number,
  activeCompanyCount: number
): {
  action: 'upgrade' | 'addon' | 'none';
  recommendedPlan?: SubscriptionPlan;
  message: string;
} {
  // Calculate what next tier would give them
  const planOrder: SubscriptionPlan[] = ['free', 'solo', 'starter', 'premium', 'enterprise'];
  const currentIndex = planOrder.indexOf(currentPlan);
  
  if (currentIndex === -1 || currentIndex === planOrder.length - 1) {
    // Already at Enterprise or unknown plan
    return {
      action: 'addon',
      message: `Add ${getCompanySlotAddonPack(currentPlan)?.slotCount || 50} more company slots`,
    };
  }
  
  const nextPlan = planOrder[currentIndex + 1];
  const nextPlanAddonPack = getCompanySlotAddonPack(nextPlan);
  const currentPlanAddonPack = getCompanySlotAddonPack(currentPlan);
  
  if (!nextPlanAddonPack || !currentPlanAddonPack) {
    return {
      action: 'none',
      message: 'Contact support for custom pricing',
    };
  }
  
  // Simple recommendation: suggest upgrade if next tier gives significantly more value
  // For now, always suggest upgrade over addon (pushes users to higher tiers)
  return {
    action: 'upgrade',
    recommendedPlan: nextPlan,
    message: `Upgrade to ${nextPlan.charAt(0).toUpperCase() + nextPlan.slice(1)} for ${nextPlanAddonPack.slotCount * 2}+ companies`,
  };
}

/**
 * Format price in cents to USD string
 */
export function formatPrice(priceInCents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  }).format(priceInCents / 100);
}

/**
 * Calculate cost impact of upgrade
 */
export function calculateUpgradeCostImpact(
  fromPlan: SubscriptionPlan,
  toPlan: SubscriptionPlan,
  userCount: number
): number {
  // This will integrate with pricingConfig to calculate actual cost difference
  // For now, placeholder that returns estimated difference
  // TODO: Integrate with actual Stripe pricing
  
  const planBasePrices: Record<SubscriptionPlan, number> = {
    free: 0,
    solo: 29900,
    starter: 49900,
    premium: 129900,
    enterprise: 499900,
  };
  
  return (planBasePrices[toPlan] || 0) - (planBasePrices[fromPlan] || 0);
}